import { Logger } from "next-axiom";

// This can be used with client or server components
// https://axiom.co/docs/apps/vercel#web-vitals
// If using with server components use `await log.flush();` just before `return <JSX />`
// As a hook you can use `const log = useLogger();` from `import { useLogger } from 'next-axiom';`

const isDev = process.env.NODE_ENV === "development";
const axiomLogger = new Logger({ autoFlush: true });

const devConsoleLevels = ["debug", "info", "warn", "error"] as const;
type LogArgs = Record<string, unknown>;

const consoleFnMap: Record<
  (typeof devConsoleLevels)[number],
  (...args: unknown[]) => void
> = {
  debug: console.debug,
  error: console.error,
  info: console.info,
  warn: console.warn
};

const withDevConsole = (logger: Logger): Logger => {
  if (!isDev) {
    return logger;
  }

  return new Proxy(logger, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);

      if (typeof value !== "function") {
        return value;
      }

      const level = prop as string;
      if (
        devConsoleLevels.includes(level as (typeof devConsoleLevels)[number])
      ) {
        return (message: string, args?: LogArgs) => {
          consoleFnMap[level as (typeof devConsoleLevels)[number]](
            `[${level.toUpperCase()}]`,
            message,
            ...(args ? [args] : [])
          );
          return (value as (...callArgs: unknown[]) => unknown).call(
            target,
            message,
            args
          );
        };
      }

      return (value as (...callArgs: unknown[]) => unknown).bind(target);
    }
  });
};

export const log = withDevConsole(axiomLogger);

export type { Logger };
